import { Clipboard, Pencil, Plus, Trash } from "lucide-react"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import {
  Button,
  Card,
  CardBody,
  Modal,
  ModalBody,
  ModalHeader,
  Table,
} from "reactstrap"

import Paginations from "components/Common/Pagination"
import { showAlert } from "../../../store/reducers/alertReducer"
import DeleteModal from "../../../components/Common/DeleteModal"
import moment from "moment/moment"
import { deleteTrialOperation } from "../../../store/actions/trialPeriodAction"
import { useDispatch } from "react-redux"
import AddCandidatesModal from "../Candidates/AddCandidatesModal"

const headers = [
  "S. No.",
  "Title",
  "Total Capacity",
  "Total User Enrolled",
  "Total Available Seat",
  "Start Date",
  "End Date",
  "Action",
  "Notes"
]

const DataTable = ({
  onPageChange,
  data,
  currentPage,
  perPage,
  totalPages,
  totalCount,
  loading,
  trigger,
  showAll,
  permissions,
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedTrailOp, setSelectedTrailOp] = useState(null)
  const [modal, setModal] = useState(false)
  const toggle = () => setModal(!modal)
  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [expandedRowId, setExpandedRowId] = useState(null)


  const handleDelete = async () => {
    dispatch(deleteTrialOperation(selectedTrailOp))
      .then(action => {
        const { statusCode, success, message, data } = action.payload
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }))
          trigger()
          setShowDeleteModal(false)
        } else {
          dispatch(showAlert({ text: message, type: "error" }))
        }
      })
      .catch(error => {})
  }

  return (
    <>
      <Card className="overflow-x-auto">
        <CardBody>
          <div className="table-responsive">
            <Table className="table mb-0">
              <thead className="table-light full_th">
                <tr>
                  {headers.map((header, index) => (
                    <th
                      key={index}
                      style={
                        header === "Title"
                          ? { minWidth: "165px", whiteSpace: "nowrap" }
                          :    header === "Start Date" ||
                          header === "End Date"
                            ? { minWidth: "99px", whiteSpace: "nowrap" }
                            : undefined
                      }
                    >
                      {t(header)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((item, index) => (
                  <tr key={item.id}>
                    <th scope="row">
                      {(currentPage - 1) * perPage + index + 1}
                    </th>
                    <td>
                      <p
                        className="text-decoration-underline cursor-pointer"
                        onClick={() => {
                          navigate(`/trial-operation/${item.uuid}/candidates`)
                        }}
                      >
                        {item.title}
                      </p>
                    </td>
                    <td>{item.total_capacity}</td>
                    <td>{item.total_user_enrolled}</td>
                    <td>{item.total_available_seat}</td>
                    <td>{item.start_date}</td>
                    <td>{item.end_date}</td>
                    <td>
                      <div className="d-flex gap-2 action_buttons">
                        {(showAll || permissions.includes("can_edit")) && (
                          <Button
                            color="secondary"
                            onClick={() => {
                              navigate(
                                `/trial-operation/update?uuid=${item.uuid}`
                              )
                            }}
                            title={t("Update")}
                          >
                            <Pencil />
                          </Button>
                        )}
                        {(showAll ||
                          permissions.includes("can_create")) && (
                          <Button
                            color="secondary"
                            onClick={() => {
                              navigate(
                                `/trial-operation/create?uuid=${item.uuid}`
                              )
                            }}
                            disabled={
                              loading &&
                              item?.uuid === itemIdToDelete
                            }
                            title={t("Clone")}
                          >
                            <Clipboard />
                          </Button>
                        )}
                        {(showAll || permissions.includes("can_delete")) && (
                          <Button
                            color="danger"
                            onClick={() => {
                              setSelectedTrailOp(item.uuid)
                              setShowDeleteModal(true)
                            }}
                            disabled={
                              loading && item.uuid === setSelectedTrailOp
                            }
                            title={t("Delete")}
                          >
                            <Trash />
                          </Button>
                        )}
                        {item?.end_date &&
                          moment(item.end_date, "DD-MM-YYYY")?.isSameOrAfter(
                            moment(),
                            "day"
                          ) &&
                          (showAll ||
                            permissions.includes(
                              "can_invite_participants"
                            )) && (
                            <Button
                              color="success"
                              onClick={() => {
                                toggle()
                                setSelectedTrailOp(item.uuid)
                              }}
                              title={t("Add Candidates")}
                            >
                              <Plus />
                            </Button>
                          )}
                      </div>
                    </td>
                    <td
                      onClick={() =>
                        setExpandedRowId(expandedRowId === item.id ? null : item.id)
                      }
                      style={{
                        maxWidth: "150px",
                        cursor: "pointer",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: expandedRowId === item.id ? "normal" : "nowrap",
                        wordBreak: "break-word"
                      }}
                      title="Click to expand"
                    >
                      {item.notes}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>
      {data.length !== 0 && (
        <Paginations
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          onPageChange={onPageChange}
        />
      )}
      <DeleteModal
        show={showDeleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => {
          setSelectedTrailOp(null)
          setShowDeleteModal(false)
        }}
        isLoading={loading}
        buttonText={t("Delete")}
        bodyTitle={t("Are you sure you want to delete this Trial Operation?")}
      />

      <Modal
        isOpen={modal}
        toggle={toggle}
        size="lg"
        className=""
        backdrop="static"
      >
        <ModalHeader toggle={toggle}>
          {t("Add Candidates In The Trial Operation")}
        </ModalHeader>
        <ModalBody>
          <AddCandidatesModal
            toggle={toggle}
            selectedUuid={selectedTrailOp}
            trigger={trigger}
          />
        </ModalBody>
      </Modal>
    </>
  )
}

export default DataTable
